import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { API_ENDPOINTS } from "@/config/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckIcon,
  XIcon,
  Sparkles,
  Search,
  ChevronLeft,
  Save,
  Loader2,
} from "lucide-react";
import "@/styles/TemplateDocument.css";

interface DetectedPlaceholder {
  text: string;
  display_name: string;
  start: number;
  end: number;
  pattern_type: string;
  confidence: number;
  source: string;
  suggested_field_type: string;
  context: string;
}

interface ReviewPlaceholder extends DetectedPlaceholder {
  accepted: boolean;
  edited_name: string;
  field_type: string;
}

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "name", label: "Name" },
  { value: "date", label: "Date" },
  { value: "amount", label: "Amount" },
  { value: "address", label: "Address" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
];

export default function PlaceholderReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [placeholders, setPlaceholders] = useState<ReviewPlaceholder[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string>("");

  // Fetch template
  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ["template-review", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("templates")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch HTML content
  useEffect(() => {
    if (!template?.processed_html) return;

    const fetchHtml = async () => {
      try {
        const { data, error } = await supabase.storage
          .from("template_html")
          .download(template.processed_html);
        if (error) throw error;
        const text = await data.text();
        setHtmlContent(text);
      } catch (err) {
        console.error("Error fetching HTML:", err);
      }
    };
    fetchHtml();
  }, [template?.processed_html]);

  // Detect placeholders when template is loaded
  useEffect(() => {
    if (!id || !template?.processed_html) return;
    detectPlaceholders();
  }, [id, template?.processed_html]);

  const detectPlaceholders = async () => {
    if (!id) return;
    setIsDetecting(true);

    try {
      const response = await fetch(API_ENDPOINTS.DETECT_PLACEHOLDERS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId: id, useAI: true }),
      });

      if (!response.ok) throw new Error("Detection failed");

      const data = await response.json();

      const reviewItems: ReviewPlaceholder[] = (
        data.placeholders || []
      ).map((p: DetectedPlaceholder) => ({
        ...p,
        accepted: p.confidence >= 0.8,
        edited_name: p.display_name,
        field_type: p.suggested_field_type,
      }));

      setPlaceholders(reviewItems);
    } catch (err) {
      console.error("Detection error:", err);
      toast({
        title: "Detection Error",
        description:
          "Could not detect placeholders. You can add them manually.",
        variant: "destructive",
      });
    } finally {
      setIsDetecting(false);
    }
  };

  const togglePlaceholder = (index: number) => {
    setPlaceholders((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, accepted: !p.accepted } : p
      )
    );
  };

  const updatePlaceholderName = (index: number, name: string) => {
    setPlaceholders((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, edited_name: name } : p
      )
    );
  };

  const updatePlaceholderType = (index: number, type: string) => {
    setPlaceholders((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, field_type: type } : p
      )
    );
  };

  const removePlaceholder = (index: number) => {
    setPlaceholders((prev) => prev.filter((_, i) => i !== index));
  };

  // Save confirmed placeholders
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No template ID");

      const accepted = placeholders.filter((p) => p.accepted);

      // Build placeholder arrays for the template record
      const placeholderNames = accepted.map(
        (p) =>
          `field_${p.edited_name
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/[^\w_]/g, "")}`
      );
      const displayNames: Record<string, string> = {};
      const placeholderTypes: Record<string, string> = {};

      accepted.forEach((p) => {
        const fieldId = `field_${p.edited_name
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^\w_]/g, "")}`;
        displayNames[fieldId] = p.edited_name;
        placeholderTypes[fieldId] = p.field_type;
      });

      // Replace detected text with [Placeholder Name] in HTML
      let processedHtml = htmlContent;
      // Sort by position descending so replacements don't shift offsets
      const sortedAccepted = [...accepted].sort(
        (a, b) => b.start - a.start
      );

      // We need to work on the plain text offsets, but the HTML has tags.
      // Instead, do text-based replacement on the HTML content.
      for (const p of sortedAccepted) {
        const escapedText = p.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(escapedText, "g");
        const placeholderTag = `<input class="placeholder-input" data-placeholder="field_${p.edited_name
          .toLowerCase()
          .replace(/\s+/g, "_")
          .replace(/[^\w_]/g, "")}" placeholder="${p.edited_name}" type="${p.field_type === "date" ? "date" : p.field_type === "number" || p.field_type === "amount" ? "number" : p.field_type === "email" ? "email" : p.field_type === "phone" ? "tel" : "text"}">`;
        processedHtml = processedHtml.replace(regex, placeholderTag);
      }

      // Upload updated HTML
      const htmlPath = (template as any).processed_html;
      await supabase.storage
        .from("template_html")
        .update(htmlPath, new Blob([processedHtml], { type: "text/html" }), {
          upsert: true,
        });

      // Update template record
      const { error } = await (supabase as any)
        .from("templates")
        .update({
          placeholders: placeholderNames,
          metadata: {
            ...(template as any)?.metadata,
            display_names: displayNames,
            placeholder_types: placeholderTypes,
            detection_source: "multi_layer",
          },
          is_approved: true,
          needs_review: false,
          processing_status: "completed",
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Template saved",
        description: "Placeholders confirmed and template is ready to use.",
      });
      navigate(`/templates/${id}/edit`);
    },
    onError: (err) => {
      toast({
        title: "Save failed",
        description:
          err instanceof Error ? err.message : "Could not save template",
        variant: "destructive",
      });
    },
  });

  const confirmedCount = placeholders.filter((p) => p.accepted).length;
  const suggestedCount = placeholders.filter((p) => !p.accepted).length;

  if (templateLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate("/templates")}
          className="mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Templates
        </Button>
        <h1 className="text-2xl font-bold">Review Placeholders</h1>
        <p className="text-gray-500 mt-1">
          Review detected placeholders, rename them, or add new ones before
          saving.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Document Preview */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Document Preview</CardTitle>
              <CardDescription>
                {(template as any)?.filename}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="border rounded-md p-6 bg-white min-h-[600px] overflow-auto template-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right: Placeholder List */}
        <div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-4 w-4" />
                Detected Placeholders
              </CardTitle>
              <CardDescription>
                {isDetecting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Detecting...
                  </span>
                ) : (
                  <>
                    <Badge variant="default" className="mr-2">
                      {confirmedCount} confirmed
                    </Badge>
                    {suggestedCount > 0 && (
                      <Badge variant="secondary">
                        {suggestedCount} suggested
                      </Badge>
                    )}
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-3">
                <div className="space-y-3">
                  {placeholders.map((p, index) => (
                    <div
                      key={index}
                      className={`border rounded-md p-3 transition-colors ${
                        p.accepted
                          ? "border-green-200 bg-green-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {p.source === "ai" && (
                            <Sparkles className="h-3 w-3 text-purple-500" />
                          )}
                          <Badge
                            variant={
                              p.confidence >= 0.9
                                ? "default"
                                : p.confidence >= 0.7
                                ? "secondary"
                                : "outline"
                            }
                            className="text-xs"
                          >
                            {Math.round(p.confidence * 100)}%
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {p.pattern_type}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => togglePlaceholder(index)}
                          >
                            <CheckIcon
                              className={`h-3 w-3 ${
                                p.accepted
                                  ? "text-green-600"
                                  : "text-gray-400"
                              }`}
                            />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => removePlaceholder(index)}
                          >
                            <XIcon className="h-3 w-3 text-red-400" />
                          </Button>
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 mb-2 truncate">
                        Found: &ldquo;{p.text}&rdquo;
                      </div>

                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs">Display Name</Label>
                          <Input
                            value={p.edited_name}
                            onChange={(e) =>
                              updatePlaceholderName(index, e.target.value)
                            }
                            className="h-7 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Field Type</Label>
                          <Select
                            value={p.field_type}
                            onValueChange={(val) =>
                              updatePlaceholderType(index, val)
                            }
                          >
                            <SelectTrigger className="h-7 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map((ft) => (
                                <SelectItem key={ft.value} value={ft.value}>
                                  {ft.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}

                  {!isDetecting && placeholders.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-8">
                      No placeholders detected. The document may not contain
                      recognizable placeholder patterns.
                    </p>
                  )}
                </div>
              </ScrollArea>

              <div className="mt-4 pt-4 border-t space-y-2">
                <Button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className="w-full"
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Confirm & Save ({confirmedCount} placeholders)
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/templates/${id}/edit`)}
                  className="w-full"
                >
                  Skip — Edit Without Placeholders
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
